/** @jsx jsx */
import React, { useEffect, useState, type MouseEvent } from 'react';
import { css, jsx } from '@compiled/react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { IconButton } from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/link';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../constants.tsx';
import messages from './messages.tsx';

export interface Props {
	issueKey: IssueKey;
}

// Exported for testing
export const COPY_SUCCESS_ICON_TIMEOUT = 3000;

export const CopyIssueLink = ({ issueKey }: Props) => {
	const { formatMessage } = useIntl();
	const [isCopied, setCopied] = useState(false);

	const handleCopy = async (e: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		e.preventDefault();
		e.stopPropagation();

		const url = typeof window !== 'undefined' ? `${window.location.origin}/browse/${issueKey}` : '';

		try {
			await globalThis.navigator.clipboard.writeText(url);
			setCopied(true);
			fireUIAnalytics(analyticsEvent, 'tableCopyIssueLink');
		} catch (error) {
			setCopied(false);
			fireErrorAnalytics({
				error: error instanceof Error ? error : undefined,
				meta: {
					id: 'copyIssueLink',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
			});
		}
	};

	useEffect(() => {
		const copyTimer = isCopied
			? setTimeout(() => setCopied(false), COPY_SUCCESS_ICON_TIMEOUT)
			: undefined;
		return () => copyTimer && clearTimeout(copyTimer);
	}, [isCopied]);

	return (
		<div css={wrapperStyles}>
			<IconButton
				appearance="subtle"
				spacing="compact"
				isTooltipDisabled={false}
				onClick={handleCopy}
				label={formatMessage(isCopied ? messages.copied : messages.copy)}
				icon={(iconProps) =>
					isCopied ? (
						<StatusSuccessIcon {...iconProps} size="small" color={token('color.icon.success')} />
					) : (
						<LinkIcon {...iconProps} size="small" />
					)
				}
			/>
		</div>
	);
};

const wrapperStyles = css({
	display: 'var(--copy-button-display, "none")',
	opacity: 'var(--copy-button-opacity, 0)',
});
